import React, { useState, useEffect } from 'react';
import { Award, BarChart3, BookOpen, Calendar, Download, Info, TrendingUp, Trophy, UserCheck, Users, Zap, PieChart } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { api } from '../api';
import { LeaderboardTable } from '../components/common/LeaderboardTable';
import { ContentPerformanceTab } from '../components/analytics/ContentPerformanceTab';

const Analytics = ({ onNavigate, user }) => {
  const isContentAdmin = user?.role === 'content_admin';
  const [activeTab, setActiveTab] = useState(isContentAdmin ? 'content-performance' : 'overview');
  const [overview, setOverview] = useState(null);
  const [downloadData, setDownloadData] = useState(null);
  const [learnerData, setLearnerData] = useState(null);
  const [partnersData, setPartnersData] = useState([]);
  const [coursesData, setCoursesData] = useState([]);
  const [courseTrends, setCourseTrends] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeDetail, setActiveDetail] = useState(null);
  const [partnerFilter, setPartnerFilter] = useState('');
  const [downloadDrilldown, setDownloadDrilldown] = useState(null); // { domain, organization, loading, users }
  // Leaderboard state
  const [solutionAreas, setSolutionAreas] = useState([]);
  const [cspArea, setCspArea] = useState('all');
  const [cspData, setCspData] = useState([]);
  const [cspLoading, setCspLoading] = useState(false);
  const [stellarPartner, setStellarPartner] = useState('');
  const [stellarArea, setStellarArea] = useState('all');
  const [stellarData, setStellarData] = useState([]);
  const [stellarLoading, setStellarLoading] = useState(false);
  const [partnerList, setPartnerList] = useState([]);
  const [velocity, setVelocity] = useState([]);
  const [velocityLoading, setVelocityLoading] = useState(false);
  const [tiers, setTiers] = useState([]);
  const [tiersLoading, setTiersLoading] = useState(false);
  // Date range filter
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [appliedFrom, setAppliedFrom] = useState('');
  const [appliedTo, setAppliedTo] = useState('');

  useEffect(() => {
    const fetchAnalytics = async () => {
      // Content authors only see the Content Performance tab — skip admin-only data calls.
      if (isContentAdmin) {
        try {
          const areasRes = await api.request('/analytics/solution-areas');
          setSolutionAreas(areasRes);
        } catch (err) {
          console.error(err);
        } finally {
          setLoading(false);
        }
        return;
      }
      const dp = new URLSearchParams();
      if (appliedFrom) dp.append('from_date', appliedFrom);
      if (appliedTo) dp.append('to_date', appliedTo);
      const qs = dp.toString() ? `?${dp.toString()}` : '';
      try {
        const [overviewRes, downloadsRes, learnersRes, usersRes, coursesRes, areasRes, partnersRes, trendsRes] = await Promise.all([
          api.request(`/analytics/overview${qs}`),
          api.request('/analytics/downloads'),
          api.request(`/analytics/learners${qs}`),
          api.request('/users?role=training_partner'),
          api.request('/courses'),
          api.request('/analytics/solution-areas'),
          api.request('/analytics/partners'),
          api.request(`/analytics/downloads/top-courses${qs}`),
        ]);
        setOverview(overviewRes);
        setDownloadData(downloadsRes);
        setLearnerData(learnersRes);
        setPartnersData(partnersRes);
        setCoursesData(coursesRes.courses || []);
        setSolutionAreas(areasRes);
        setCourseTrends(trendsRes);
        // Build partner domain list for stellar reseller filter
        const domains = [...new Set(usersRes.map(u => u.domain).filter(Boolean))];
        setPartnerList(domains);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchAnalytics();
  }, [appliedFrom, appliedTo]);

  // Fetch CSP leaderboard when tab or area changes
  useEffect(() => {
    if (activeTab === 'csp-leaderboard') {
      setCspLoading(true);
      api.request(`/analytics/csp-leaderboard?solution_area=${cspArea}`)
        .then(d => { setCspData(d); setCspLoading(false); }).catch(() => setCspLoading(false));
    }
  }, [activeTab, cspArea]);

  // Fetch Stellar Reseller
  useEffect(() => {
    if (activeTab === 'stellar-reseller') {
      setStellarLoading(true);
      const params = new URLSearchParams({ solution_area: stellarArea });
      if (stellarPartner) params.append('partner_domain', stellarPartner);
      api.request(`/analytics/stellar-reseller-leaderboard?${params.toString()}`)
        .then(d => { setStellarData(d); setStellarLoading(false); }).catch(() => setStellarLoading(false));
    }
  }, [activeTab, stellarArea, stellarPartner]);

  // Fetch velocity + tiers lazily, velocity re-fetches on date range change
  useEffect(() => {
    if (activeTab === 'training-velocity') {
      setVelocityLoading(true);
      const dp = new URLSearchParams();
      if (appliedFrom) dp.append('from_date', appliedFrom);
      if (appliedTo) dp.append('to_date', appliedTo);
      const qs = dp.toString() ? `?${dp.toString()}` : '';
      api.request(`/analytics/training-velocity${qs}`).then(d => { setVelocity(d); setVelocityLoading(false); }).catch(() => setVelocityLoading(false));
    }
    if (activeTab === 'partner-tiers') {
      setTiersLoading(true);
      api.request('/analytics/partner-tiers').then(d => { setTiers(d); setTiersLoading(false); }).catch(() => setTiersLoading(false));
    }
  }, [activeTab, appliedFrom, appliedTo]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-10 h-10 border-4 border-levelup-accent border-t-transparent rounded-full"></div>
      </div>
    );
  }

  const ClickableStatCard = ({ icon: Icon, label, value, color, onClick }) => (
    <div className="bg-white rounded-xl shadow-md p-4 card-hover border-l-4 cursor-pointer hover:shadow-lg transition-shadow" style={{ borderLeftColor: color }} onClick={onClick} data-testid={`stat-card-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center justify-between">
        <div><p className="text-gray-500 text-sm">{label}</p><p className="text-2xl font-bold text-levelup-dark mt-1">{value}</p></div>
        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}20` }}><Icon style={{ color }} size={20} /></div>
      </div>
      <p className="text-xs text-gray-400 mt-2">Click to view details →</p>
    </div>
  );

  const filteredPartners = partnersData.filter(p => !partnerFilter || p.organization?.toLowerCase().includes(partnerFilter.toLowerCase()) || p.domain?.toLowerCase().includes(partnerFilter.toLowerCase()));

  const openDownloadDrilldown = async (partner) => {
    setDownloadDrilldown({ domain: partner.domain, organization: partner.organization || partner.domain, loading: true, users: [] });
    try {
      const users = await api.request(`/analytics/downloads/by-domain/${encodeURIComponent(partner.domain)}`);
      setDownloadDrilldown({ domain: partner.domain, organization: partner.organization || partner.domain, loading: false, users });
    } catch (err) {
      setDownloadDrilldown({ domain: partner.domain, organization: partner.organization || partner.domain, loading: false, users: [], error: err.message });
    }
  };

  // Detail view
  if (activeDetail === 'courses') {
    return (
      <div className="space-y-4">
        <div className="bg-levelup-dark rounded-xl p-4 text-white"><div className="flex items-center justify-between"><div><h1 className="text-xl font-bold">All Courses</h1><p className="text-gray-300 mt-1">Complete list of available courses</p></div><button onClick={() => setActiveDetail(null)} className="px-4 py-2 bg-levelup-accent text-levelup-darker rounded-lg hover:bg-levelup-accent-hover">← Back to Analytics</button></div></div>
        <div className="bg-white rounded-xl shadow-md p-4"><table className="w-full"><thead className="bg-gray-50"><tr><th className="px-3 py-2 text-left text-sm font-semibold text-gray-700">Course Title</th><th className="px-3 py-2 text-left text-sm font-semibold text-gray-700">Solution Area</th><th className="px-3 py-2 text-left text-sm font-semibold text-gray-700">Level</th><th className="px-3 py-2 text-left text-sm font-semibold text-gray-700">Files</th></tr></thead><tbody className="divide-y">{coursesData.map(course => (<tr key={course.id}><td className="px-3 py-2 font-medium">{course.title}</td><td className="px-3 py-2 text-gray-600">{course.solution_area}</td><td className="px-3 py-2 text-gray-600">{course.level}</td><td className="px-3 py-2 text-gray-600">{course.files?.length || 0}</td></tr>))}</tbody></table></div>
      </div>
    );
  }
  if (activeDetail === 'partners' || activeDetail === 'downloads') {
    const activePartnersCount = partnersData.filter(p => (p.total_downloads || 0) > 0).length;
    return (
      <div className="space-y-4">
        <div className="bg-levelup-dark rounded-xl p-4 text-white"><div className="flex items-center justify-between"><div><h1 className="text-xl font-bold">Program Partners</h1><p className="text-gray-300 mt-1">Partner organizations and their activity</p></div><button onClick={() => setActiveDetail(null)} className="px-4 py-2 bg-levelup-accent text-levelup-darker rounded-lg hover:bg-levelup-accent-hover">← Back to Analytics</button></div></div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl shadow-md p-4 border-l-4" style={{ borderLeftColor: '#9333EA' }} data-testid="stat-active-partners-downloading">
            <p className="text-gray-500 text-sm">Active Partners Downloading Content</p>
            <p className="text-2xl font-bold text-levelup-dark mt-1">{activePartnersCount}</p>
          </div>
          <div className="bg-white rounded-xl shadow-md p-4 border-l-4" style={{ borderLeftColor: '#0D3B36' }}>
            <p className="text-gray-500 text-sm">Total Partner Domains</p>
            <p className="text-2xl font-bold text-levelup-dark mt-1">{partnersData.length}</p>
          </div>
          <div className="bg-white rounded-xl shadow-md p-4 border-l-4" style={{ borderLeftColor: '#F97316' }}>
            <p className="text-gray-500 text-sm">Total Downloads (All Partners)</p>
            <p className="text-2xl font-bold text-levelup-dark mt-1">{partnersData.reduce((sum, p) => sum + (p.total_downloads || 0), 0)}</p>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-md p-4">
          <div className="mb-4"><input type="text" placeholder="Filter by partner name or domain..." value={partnerFilter} onChange={(e) => setPartnerFilter(e.target.value)} className="w-full md:w-64 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-levelup-accent" data-testid="partner-filter-input" /></div>
          <table className="w-full"><thead className="bg-gray-50"><tr><th className="px-3 py-2 text-left text-sm font-semibold text-gray-700">Company Name</th><th className="px-3 py-2 text-left text-sm font-semibold text-gray-700">Domain</th><th className="px-3 py-2 text-left text-sm font-semibold text-gray-700">Trainers</th><th className="px-3 py-2 text-left text-sm font-semibold text-gray-700">Total Downloads</th></tr></thead><tbody className="divide-y">{filteredPartners.sort((a, b) => (b.total_downloads || 0) - (a.total_downloads || 0)).map(partner => (<tr key={partner.domain}><td className="px-3 py-2 font-medium">{partner.organization || partner.domain || '-'}</td><td className="px-3 py-2 text-gray-600">{partner.domain || '-'}</td><td className="px-3 py-2 text-gray-600">{partner.trainers || 0}</td><td className="px-3 py-2"><button onClick={() => openDownloadDrilldown(partner)} className="font-bold text-levelup-accent hover:underline cursor-pointer" data-testid={`downloads-drilldown-${partner.domain}`}>{partner.total_downloads || 0}</button></td></tr>))}</tbody></table>
          {filteredPartners.length === 0 && <p className="text-gray-500 text-center py-4">No partners found</p>}
        </div>
        {downloadDrilldown && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="downloads-drilldown-modal">
            <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6 max-h-[80vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-levelup-dark">Downloads by User — {downloadDrilldown.organization}</h3>
                <button onClick={() => setDownloadDrilldown(null)} className="text-gray-400 hover:text-gray-600" data-testid="downloads-drilldown-close">✕</button>
              </div>
              {downloadDrilldown.loading ? (
                <div className="animate-spin w-8 h-8 border-4 border-levelup-accent border-t-transparent rounded-full mx-auto"></div>
              ) : downloadDrilldown.error ? (
                <p className="text-red-600 text-sm">{downloadDrilldown.error}</p>
              ) : downloadDrilldown.users.length === 0 ? (
                <p className="text-gray-500 text-sm text-center py-4">No users found for this domain.</p>
              ) : (
                <table className="w-full">
                  <thead><tr><th className="px-2 py-1.5 text-left text-sm font-semibold text-gray-700">User</th><th className="px-2 py-1.5 text-right text-sm font-semibold text-gray-700">Downloads</th></tr></thead>
                  <tbody className="divide-y">
                    {downloadDrilldown.users.map(u => (
                      <tr key={u.user_id}>
                        <td className="px-2 py-1.5"><p className="font-medium text-sm">{u.full_name || u.email}</p><p className="text-xs text-gray-500">{u.email}</p></td>
                        <td className="px-2 py-1.5 text-right font-bold text-levelup-accent">{u.download_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  const analyticsTabs = isContentAdmin ? [
    { id: 'content-performance', label: 'Content Performance', icon: PieChart },
  ] : [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'content-performance', label: 'Content Performance', icon: PieChart },
    { id: 'csp-leaderboard', label: 'CSP Leaderboard', icon: Trophy },
    { id: 'stellar-reseller', label: 'Stellar Reseller', icon: Award },
    { id: 'training-velocity', label: 'Training Velocity', icon: TrendingUp },
    { id: 'partner-tiers', label: 'Partner Tiers', icon: Zap },
  ];

  return (
    <div className="space-y-4">
      <div className="bg-levelup-dark rounded-xl p-4 text-white">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-xl font-bold">Analytics Dashboard</h1>
            <p className="text-gray-300 mt-1">Insights on content usage, learner engagement, and partner performance</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-2 bg-white/10 rounded-lg px-3 py-2 border border-white/20">
              <Calendar size={14} className="text-gray-300 flex-shrink-0" />
              <span className="text-xs text-gray-400 hidden sm:inline">From</span>
              <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
                className="text-sm bg-transparent text-white border-none outline-none w-32 [color-scheme:dark]"
                data-testid="analytics-date-from" />
              <span className="text-gray-400 text-xs">–</span>
              <span className="text-xs text-gray-400 hidden sm:inline">To</span>
              <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
                className="text-sm bg-transparent text-white border-none outline-none w-32 [color-scheme:dark]"
                data-testid="analytics-date-to" />
              <button
                onClick={() => { setAppliedFrom(fromDate); setAppliedTo(toDate); }}
                className="ml-1 px-3 py-1 bg-levelup-accent text-levelup-darker text-xs font-bold rounded hover:bg-levelup-accent-hover transition-colors"
                data-testid="analytics-date-apply">Apply</button>
              {(appliedFrom || appliedTo) && (
                <button
                  onClick={() => { setFromDate(''); setToDate(''); setAppliedFrom(''); setAppliedTo(''); }}
                  className="text-gray-400 hover:text-red-400 text-xs transition-colors ml-1"
                  data-testid="analytics-date-clear">Clear</button>
              )}
            </div>
            {(appliedFrom || appliedTo) && (
              <span className="text-xs bg-levelup-accent/20 text-levelup-accent border border-levelup-accent/40 rounded-full px-3 py-1" data-testid="analytics-date-badge">
                {appliedFrom && appliedTo ? `${appliedFrom} → ${appliedTo}` : appliedFrom ? `From ${appliedFrom}` : `Until ${appliedTo}`}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex flex-wrap gap-2">
        {analyticsTabs.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} data-testid={`analytics-tab-${tab.id}`}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${activeTab === tab.id ? 'bg-levelup-dark text-white' : 'bg-white shadow-sm text-gray-600 hover:bg-gray-50 border border-gray-200'}`}>
            <tab.icon size={16} />{tab.label}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <>
          {(appliedFrom || appliedTo) && (
            <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2 text-sm text-amber-800" data-testid="overview-date-filter-note">
              <Info size={14} className="text-amber-500 flex-shrink-0" />
              Workshop, learner and download KPIs are filtered to{' '}
              {appliedFrom && <><strong>{appliedFrom}</strong>{appliedTo && ' → '}</>}
              {appliedTo && <strong>{appliedTo}</strong>}.
              Partner and course counts always show current totals.
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <ClickableStatCard icon={BookOpen} label="Total Courses" value={overview?.total_courses || 0} color="#0D3B36" onClick={() => setActiveDetail('courses')} />
            <ClickableStatCard icon={Users} label="Partner Trainers" value={overview?.total_partners || 0} color="#C4D600" onClick={() => setActiveDetail('partners')} />
            <ClickableStatCard icon={Download} label="Total Downloads" value={overview?.total_downloads || 0} color="#9333EA" onClick={() => setActiveDetail('downloads')} />
            <ClickableStatCard icon={Calendar} label="Workshops" value={overview?.total_workshops || 0} color="#F97316" onClick={() => onNavigate && onNavigate('workshops')} />
            <ClickableStatCard icon={UserCheck} label="Trained Learners" value={overview?.total_trained_learners || 0} color="#EC4899" onClick={() => onNavigate && onNavigate('workshops')} />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl shadow-md p-4">
              <h2 className="text-lg font-semibold text-levelup-dark mb-4">Top Downloaded Courses</h2>
              <div className="space-y-3">
                {downloadData?.by_course?.slice(0, 5).map((course, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3"><span className="w-8 h-8 bg-levelup-dark text-white rounded-full flex items-center justify-center text-sm font-bold">{idx + 1}</span><span className="font-medium text-gray-700 truncate max-w-[200px]">{course.course_title}</span></div>
                    <span className="text-levelup-dark font-semibold">{course.download_count}</span>
                  </div>
                ))}
                {(!downloadData?.by_course || downloadData.by_course.length === 0) && <p className="text-gray-500 text-center py-4">No download data yet</p>}
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-md p-4">
              <h2 className="text-lg font-semibold text-levelup-dark mb-4">Learners by Organization</h2>
              <div className="space-y-3">
                {learnerData?.by_organization?.slice(0, 5).map((org, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div><p className="font-medium text-gray-700">{org._id}</p><p className="text-sm text-gray-500">{org.total_workshops} workshops | {(org.avg_completion_rate || 0).toFixed(1)}% completion</p></div>
                    <div className="text-right"><p className="text-xl font-bold text-levelup-accent">{org.total_learners}</p><p className="text-xs text-gray-500">learners</p></div>
                  </div>
                ))}
                {(!learnerData?.by_organization || learnerData.by_organization.length === 0) && <p className="text-gray-500 text-center py-4">No learner data yet</p>}
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-md p-4" data-testid="course-download-trends-card">
            <div className="flex items-center justify-between mb-1">
              <h2 className="text-lg font-semibold text-levelup-dark">Course Download Trends (Top 10)</h2>
              <span className="text-xs text-gray-500" data-testid="course-download-trends-range">
                {appliedFrom || appliedTo
                  ? `${appliedFrom || 'earliest'} → ${appliedTo || 'latest'}`
                  : 'Last 30 days (default)'}
              </span>
            </div>
            {courseTrends?.top_courses?.length > 0 ? (
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={courseTrends.top_courses} margin={{ top: 10, right: 10, left: 0, bottom: 80 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                  <XAxis
                    dataKey="course_title"
                    angle={-35}
                    textAnchor="end"
                    interval={0}
                    height={90}
                    tick={{ fontSize: 11, fill: '#4b5563' }}
                    tickFormatter={(title) => title.length > 20 ? `${title.slice(0, 20)}…` : title}
                  />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: '#4b5563' }} />
                  <Tooltip formatter={(value) => [value, 'Downloads']} labelStyle={{ color: '#0D3B36', fontWeight: 600 }} />
                  <Bar dataKey="download_count" fill="#9333EA" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-gray-500 text-center py-12">No download data in this period.</p>
            )}
          </div>
          <div className="bg-white rounded-xl shadow-md p-4">
            <h2 className="text-lg font-semibold text-levelup-dark mb-4">Top Courses by Trained Learners</h2>
            <div className="overflow-x-auto">
              <table className="w-full"><thead className="bg-gray-50"><tr><th className="px-3 py-2 text-left text-sm font-semibold text-gray-700">Course</th><th className="px-3 py-2 text-left text-sm font-semibold text-gray-700">Workshops</th><th className="px-3 py-2 text-left text-sm font-semibold text-gray-700">Trained Learners</th></tr></thead>
              <tbody className="divide-y">{learnerData?.by_course?.map((course, idx) => (<tr key={idx}><td className="px-3 py-2 font-medium">{course.course_title}</td><td className="px-3 py-2 text-gray-600">{course.total_workshops}</td><td className="px-3 py-2"><span className="font-bold text-levelup-accent">{course.total_learners}</span></td></tr>))}</tbody></table>
              {(!learnerData?.by_course || learnerData.by_course.length === 0) && <p className="text-gray-500 text-center py-4">No course learner data yet</p>}
            </div>
          </div>
        </>
      )}

      {/* Content Performance Tab */}
      {activeTab === 'content-performance' && (
        <ContentPerformanceTab appliedFrom={appliedFrom} appliedTo={appliedTo} solutionAreas={solutionAreas} />
      )}

      {/* CSP Leaderboard Tab */}
      {activeTab === 'csp-leaderboard' && (
        <div className="bg-white rounded-xl shadow-md p-4">
          <div className="flex items-center gap-3 mb-2"><Trophy size={20} className="text-levelup-accent" /><h2 className="text-xl font-bold text-levelup-dark">CSP Leaderboard — Top Partners</h2></div>
          <p className="text-sm text-gray-500 mb-6">CSP partners ranked by the number of resellers they have trained across their delivered workshops</p>
          <LeaderboardTable data={cspData} domainKey="partner_domain" loading={cspLoading} solutionAreas={solutionAreas} selectedArea={cspArea} onAreaChange={setCspArea} />
        </div>
      )}

      {/* Stellar Reseller Tab */}
      {activeTab === 'stellar-reseller' && (
        <div className="bg-white rounded-xl shadow-md p-4">
          <div className="flex items-center gap-3 mb-2"><Award size={20} className="text-levelup-accent" /><h2 className="text-xl font-bold text-levelup-dark">Stellar Reseller Leaderboard</h2></div>
          <p className="text-sm text-gray-500 mb-4">Top resellers ranked by training attendance. Filter by CSP partner to view their reseller network.</p>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Filter by CSP Partner</label>
            <select value={stellarPartner} onChange={e => setStellarPartner(e.target.value)} className="w-full md:w-72 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-levelup-accent" data-testid="stellar-partner-filter">
              <option value="">All Partners</option>
              {partnerList.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <LeaderboardTable data={stellarData} domainKey="reseller_domain" loading={stellarLoading} solutionAreas={solutionAreas} selectedArea={stellarArea} onAreaChange={setStellarArea} />
        </div>
      )}

      {/* Training Velocity Tab */}
      {activeTab === 'training-velocity' && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl shadow-md p-4">
            <div className="flex items-center gap-3 mb-4"><TrendingUp size={22} className="text-levelup-accent" /><h2 className="text-lg font-bold text-levelup-dark">Monthly Training Velocity</h2></div>
            <p className="text-sm text-gray-500 mb-6">
              Workshops delivered and learners trained per month
              {appliedFrom || appliedTo
                ? ` (${appliedFrom && appliedTo ? `${appliedFrom} → ${appliedTo}` : appliedFrom ? `from ${appliedFrom}` : `until ${appliedTo}`})`
                : ' (last 12 months)'}
            </p>
            {velocityLoading ? <div className="flex justify-center py-10"><div className="animate-spin w-8 h-8 border-4 border-levelup-accent border-t-transparent rounded-full" /></div> : velocity.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No completed workshop data yet.</p>
            ) : (
              <>
                <div className="flex items-start gap-6 mb-4 text-xs text-gray-500 font-medium">
                  <span className="flex items-center gap-1"><span className="w-3 h-3 bg-levelup-dark rounded-sm inline-block" /> Workshops</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 bg-levelup-accent rounded-sm inline-block" /> Learners</span>
                </div>
                <div className="space-y-4">
                  {velocity.map((m, i) => {
                    const maxWs = Math.max(...velocity.map(v => v.workshops));
                    const maxLr = Math.max(...velocity.map(v => v.learners));
                    return (
                      <div key={i} className="flex items-start gap-4">
                        <span className="text-sm text-gray-500 w-16 flex-shrink-0 pt-1">{m.month}</span>
                        <div className="flex-1 space-y-1.5">
                          <div className="flex items-center gap-2">
                            <div className="bg-levelup-dark h-6 rounded text-white text-xs flex items-center px-2 font-medium" style={{ width: `${Math.max((m.workshops / maxWs) * 300, 32)}px` }}>{m.workshops} ws</div>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="bg-levelup-accent h-6 rounded text-levelup-darker text-xs flex items-center px-2 font-medium" style={{ width: `${Math.max((m.learners / maxLr) * 300, 32)}px` }}>{m.learners} learners</div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
          {velocity.length > 0 && (
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-white rounded-xl shadow-md p-5 border-l-4 border-levelup-dark"><p className="text-gray-500 text-sm">Total Workshops (12mo)</p><p className="text-2xl font-bold text-levelup-dark">{velocity.reduce((s, v) => s + v.workshops, 0)}</p></div>
              <div className="bg-white rounded-xl shadow-md p-5 border-l-4 border-levelup-accent"><p className="text-gray-500 text-sm">Total Learners (12mo)</p><p className="text-2xl font-bold text-levelup-dark">{velocity.reduce((s, v) => s + v.learners, 0)}</p></div>
              <div className="bg-white rounded-xl shadow-md p-5 border-l-4 border-purple-500"><p className="text-gray-500 text-sm">Avg Learners / Workshop</p><p className="text-2xl font-bold text-levelup-dark">{velocity.reduce((s, v) => s + v.workshops, 0) > 0 ? Math.round(velocity.reduce((s, v) => s + v.learners, 0) / velocity.reduce((s, v) => s + v.workshops, 0)) : 0}</p></div>
            </div>
          )}
        </div>
      )}

      {/* Partner Tiers Tab */}
      {activeTab === 'partner-tiers' && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl shadow-md p-4">
            <div className="flex items-center gap-3 mb-2"><Zap size={22} className="text-levelup-accent" /><h2 className="text-lg font-bold text-levelup-dark">Partner Engagement Tiers</h2></div>
            <p className="text-sm text-gray-500 mb-4">Partners auto-classified by training score. Champion ≥ 80pts | Accelerator ≥ 40pts | Explorer &lt; 40pts</p>
            <div className="flex items-start gap-2 bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-6 text-sm">
              <Info size={15} className="text-emerald-600 mt-0.5 flex-shrink-0" />
              <span className="text-emerald-800">Score = Learners Trained + (Workshops × 3). Tiers reward sustained delivery and broad reach.</span>
            </div>
            {tiersLoading ? <div className="flex justify-center py-10"><div className="animate-spin w-8 h-8 border-4 border-levelup-accent border-t-transparent rounded-full" /></div> : (
              <>
                <div className="grid md:grid-cols-3 gap-4 mb-6">
                  {[['Champion', '#F59E0B', tiers.filter(t => t.tier === 'Champion')], ['Accelerator', '#3B82F6', tiers.filter(t => t.tier === 'Accelerator')], ['Explorer', '#10B981', tiers.filter(t => t.tier === 'Explorer')]].map(([tier, color, members]) => (
                    <div key={tier} className="rounded-xl p-4 border-2" style={{ borderColor: color, backgroundColor: `${color}10` }}>
                      <p className="font-bold text-lg" style={{ color }}>{tier}</p>
                      <p className="text-2xl font-bold text-levelup-dark">{members.length}</p>
                      <p className="text-sm text-gray-500">partners</p>
                    </div>
                  ))}
                </div>
                <div className="overflow-x-auto rounded-xl border border-gray-200">
                  <table className="w-full">
                    <thead><tr className="bg-levelup-dark text-white"><th className="px-3 py-2 text-left text-sm font-semibold">Tier</th><th className="px-3 py-2 text-left text-sm font-semibold">Partner Domain</th><th className="px-3 py-2 text-left text-sm font-semibold">Score</th><th className="px-3 py-2 text-left text-sm font-semibold">Workshops</th><th className="px-3 py-2 text-left text-sm font-semibold">Learners</th></tr></thead>
                    <tbody className="divide-y">
                      {tiers.map((t, i) => (
                        <tr key={i} className="hover:bg-gray-50">
                          <td className="px-3 py-2">
                            <span className={`px-2 py-1 rounded text-xs font-bold ${t.tier === 'Champion' ? 'bg-yellow-100 text-yellow-700' : t.tier === 'Accelerator' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>{t.tier}</span>
                          </td>
                          <td className="px-3 py-2 font-medium text-levelup-dark">{t.partner_domain}</td>
                          <td className="px-3 py-2 font-bold">{t.score}</td>
                          <td className="px-3 py-2">{t.workshops}</td>
                          <td className="px-3 py-2 font-bold text-levelup-teal">{t.learners}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {tiers.length === 0 && <p className="text-gray-500 text-center py-8">No partner data yet.</p>}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Analytics;
